import React, { useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  FormControlLabel,
  Switch,
  IconButton,
  Tooltip,
  Chip,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { SlotTimePicker } from './SlotTimePicker';
import { TimeRange } from '../../types';
import {
  calculateRangeSlotInfo,
  isRangeSlotPerfectFit,
  RangeValidationError,
  WrapPartners,
} from '../../utils';

const LAST_MINUTE_OF_DAY = 23 * 60 + 59;

const buildNextRange = (lastRange: TimeRange | undefined, duration: number): TimeRange => {
  if (!lastRange?.end_time || !lastRange?.start_time) {
    return { start_time: '', end_time: '' };
  }

  if (lastRange.end_time === '23:59') {
    return { start_time: '', end_time: '' };
  }

  const [h, m] = lastRange.end_time.split(':').map(Number);
  const endMins = h * 60 + m + duration;
  const end_time = endMins > LAST_MINUTE_OF_DAY
    ? ''
    : `${String(Math.floor(endMins / 60)).padStart(2, '0')}:${String(endMins % 60).padStart(2, '0')}`;

  return {
    start_time: lastRange.end_time,
    end_time,
  };
};

interface DayTimeRangesEditorProps {
  dayLabel: string;
  dayValue: number;
  isActive: boolean;
  ranges: TimeRange[];
  duration: number;
  onToggle: () => void;
  onRangesChange: (ranges: TimeRange[]) => void;
  errors?: Record<string, string | null>;
  rangeErrors?: RangeValidationError[];
  wrapPartners?: WrapPartners;
}

export const DayTimeRangesEditor: React.FC<DayTimeRangesEditorProps> = ({
  dayLabel,
  dayValue,
  isActive,
  ranges,
  duration,
  onToggle,
  onRangesChange,
  errors = {},
  rangeErrors = [],
  wrapPartners,
}) => {
  const slotInfo = useMemo(
    () => calculateRangeSlotInfo(ranges, duration, wrapPartners),
    [ranges, duration, wrapPartners]
  );
  const { totalMinutes, slots, remainder } = slotInfo;
  const hasBookableTime = slots > 0;
  const hasPerfectFit = remainder === 0 && hasBookableTime;

  const handleRangeChange = useCallback(
    (index: number, field: 'start_time' | 'end_time', value: string) => {
      const newRanges = ranges.map((r, i) => (i === index ? { ...r, [field]: value } : r));
      onRangesChange(newRanges);
    },
    [ranges, onRangesChange]
  );

  const handleAddRange = useCallback(() => {
    const lastRange = ranges[ranges.length - 1];
    onRangesChange([...ranges, buildNextRange(lastRange, duration)]);
  }, [duration, ranges, onRangesChange]);

  const handleDeleteRange = useCallback(
    (index: number) => {
      onRangesChange(ranges.filter((_, i) => i !== index));
    },
    [ranges, onRangesChange]
  );

  const getRangeError = useCallback(
    (index: number): string | null => {
      const error = rangeErrors.find((e) => e.index === index);
      return error ? error.message : null;
    },
    [rangeErrors]
  );

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        minHeight: 48,
        py: 1,
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&:last-child': { borderBottom: 'none' },
      }}
    >
      <FormControlLabel
        control={
          <Switch
            checked={!!isActive}
            onChange={onToggle}
            size="small"
            color="primary"
          />
        }
        label={
          <Typography sx={{ width: 45, fontWeight: isActive ? 600 : 400 }}>
            {dayLabel}
          </Typography>
        }
        sx={{ m: 0, mr: 2, mt: 0.5 }}
      />

      {isActive ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, py: 0.5 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {ranges.map((range, index) => {
              const isFit = isRangeSlotPerfectFit(range, duration, wrapPartners, ranges);

              return (
                <Box
                  key={index}
                  data-booking-error-key={`days_info_slot_${dayValue}_${index}`}
                  sx={{
                    display: 'flex',
                    alignItems: { xs: 'stretch', sm: 'center' },
                    flexWrap: 'wrap',
                    gap: 1,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: { xs: 'stretch', sm: 'center' },
                      flexWrap: 'wrap',
                      gap: 1,
                      flex: { xs: '1 1 100%', sm: '0 0 auto' },
                    }}
                  >
                    <SlotTimePicker
                      size="small"
                      value={range.start_time || ''}
                      onChange={(val) => handleRangeChange(index, 'start_time', val)}
                      duration={duration}
                      sx={{ width: { xs: '100%', sm: 150 }, minWidth: 140 }}
                    />
                    <Typography color="textSecondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
                      -
                    </Typography>
                    <SlotTimePicker
                      size="small"
                      value={range.end_time || ''}
                      onChange={(val) => handleRangeChange(index, 'end_time', val)}
                      duration={duration}
                      isEndTime
                      startTime={range.start_time}
                      showPerfectFit
                      showPerfectFitIndicator={false}
                      allowMidnightWrap
                      rangeIndex={index}
                      ranges={ranges}
                      wrapPartners={wrapPartners}
                      sx={{ width: { xs: '100%', sm: 150 }, minWidth: 140 }}
                    />
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      flexWrap: 'nowrap',
                    }}
                  >
                    <Tooltip
                      title={
                        isFit
                          ? 'Perfect fit - all time is bookable'
                          : 'Adjust to eliminate remainder time'
                      }
                    >
                      <Chip
                        size="small"
                        color={isFit ? 'success' : 'warning'}
                        variant="outlined"
                        label={isFit ? 'Fit' : 'Adjust'}
                        sx={{
                          height: 24,
                          minWidth: 64,
                          fontSize: '0.75rem',
                          '& .MuiChip-label': {
                            width: '100%',
                            textAlign: 'center',
                            px: 1,
                          },
                        }}
                      />
                    </Tooltip>

                    <Tooltip title="Delete range">
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteRange(index)}
                        sx={{ color: 'error.main' }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>

                    {index === ranges.length - 1 && (
                      <Tooltip title="Add another range">
                        <IconButton
                          size="small"
                          onClick={handleAddRange}
                          sx={{
                            color: 'primary.main',
                            border: '1px dashed',
                            borderColor: 'primary.main',
                            borderRadius: 1,
                            '&:hover': { borderStyle: 'solid' },
                          }}
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>

                  {getRangeError(index) && (
                    <Typography variant="caption" color="error" sx={{ width: '100%', mt: 0.5 }}>
                      {getRangeError(index)}
                    </Typography>
                  )}
                </Box>
              );
            })}

            {ranges.length === 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  No time ranges set
                </Typography>
                <Tooltip title="Add a time range">
                  <IconButton
                    size="small"
                    onClick={handleAddRange}
                    sx={{
                      color: 'primary.main',
                      border: '1px dashed',
                      borderColor: 'primary.main',
                      borderRadius: 1,
                      '&:hover': { borderStyle: 'solid' },
                    }}
                  >
                    <AddIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          </Box>

          {(totalMinutes > 0 || remainder > 0) && (
            <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant="caption" color="text.primary" fontWeight={700}>
                Total:
              </Typography>
              <Typography
                variant="caption"
                color={hasPerfectFit ? 'success.main' : 'warning.main'}
                fontWeight={500}
              >
                {slots} slots
                {!hasPerfectFit && `, ${remainder} min unschedulable`}
              </Typography>
            </Box>
          )}

          {errors[`day_${dayValue}`] && (
            <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
              {errors[`day_${dayValue}`]}
            </Typography>
          )}
        </Box>
      ) : (
        <Typography
          variant="body2"
          color="textSecondary"
          sx={{ fontStyle: 'italic', mt: 0.5 }}
        >
          Unavailable
        </Typography>
      )}
    </Box>
  );
};
